import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Loader2, Search } from "lucide-react";

const kName = (k) => [k.vorname, k.nachname].filter(Boolean).join(" ");
const kOrt = (k) => (k.lieferadresse?.ort || k.rechnungsadresse?.ort || "");

// Typeahead-Suche im Kundenstamm (Name, Firma, Kundennummer, E-Mail, Ort) – entprellt, Tastatur: ↑ ↓ Enter Esc
export function KundeSuche({ onSelect, testId = "kunde-suche", placeholder = "Name, Firma, Kundennr., E-Mail oder Ort", inputId }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(0);
  const seq = useRef(0);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setItems([]); return undefined; }
    const my = ++seq.current;
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await api.get("/kunden", { params: { q: term, limit: 8 } });
        if (my === seq.current) { setItems(r.data); setActive(0); setOpen(true); }
      } catch { if (my === seq.current) setItems([]); }
      finally { if (my === seq.current) setBusy(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const pick = (k) => { setOpen(false); setQ(""); setItems([]); onSelect(k); };
  const onKey = (e) => {
    if (!open || !items.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % items.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a - 1 + items.length) % items.length); }
    else if (e.key === "Enter") { e.preventDefault(); pick(items[active]); }
    else if (e.key === "Escape") { e.stopPropagation(); setOpen(false); }
  };

  return (
    <div className="relative">
      <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-2.5 pointer-events-none" />
      <Input id={inputId} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} onFocus={() => items.length && setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder} className="pl-8" role="combobox" aria-expanded={open} aria-controls={`${testId}-list`}
        aria-label="Bestehenden Kunden suchen" data-testid={`${testId}-input`} />
      {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-400 absolute right-2.5 top-2.5" />}
      {open && (
        <ul id={`${testId}-list`} role="listbox" className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg" data-testid={`${testId}-list`}>
          {!items.length && <li className="px-3 py-2 text-sm text-slate-500" data-testid={`${testId}-empty`}>Kein Kunde gefunden.</li>}
          {items.map((k, i) => (
            <li key={k.id} role="option" aria-selected={i === active} onMouseDown={(e) => { e.preventDefault(); pick(k); }} onMouseEnter={() => setActive(i)}
              className={`px-3 py-2 cursor-pointer text-sm ${i === active ? "bg-slate-100" : ""}`} data-testid={`${testId}-option-${k.id}`}>
              <div className="flex justify-between gap-2"><span className="font-medium text-slate-900 truncate">{k.firma || kName(k) || k.email}</span><span className="font-mono text-xs text-slate-500 shrink-0">{k.kundennummer}</span></div>
              <div className="text-xs text-slate-500 truncate">{[k.firma ? kName(k) : "", kOrt(k), k.email].filter(Boolean).join(" · ")}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
